// src/AppProvider.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@/core/contexts/ThemeContext';
import { AuthProvider } from '@/core/contexts/AuthContext';
import { AuthConfigProvider } from '@/core/contexts/AuthConfigContext';
import { NotificationProvider } from '@/core/contexts/NotificationContext';
import SessionTimeoutHandler from '@/components/common/SessionTimeoutHandler';
import queryClient from '@/core/config/queryClient';
import { SnackbarProvider } from 'notistack';

/**
 * Application provider component that wraps the app with necessary providers
 * - BrowserRouter for routing
 * - ThemeProvider for Material-UI theming
 * - QueryClientProvider for React Query
 * - AuthConfigProvider for centralized auth configuration (single API call)
 * - NotificationProvider for app-wide notifications
 * - AuthProvider for authentication state (handles lookup/preference loading)
 */
const AppProvider = ({ children }) => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <AuthConfigProvider>
            <NotificationProvider>
              <SnackbarProvider
                maxSnack={4}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <AuthProvider>
                  {children}
                  <SessionTimeoutHandler />
                </AuthProvider>
              </SnackbarProvider>
            </NotificationProvider>
          </AuthConfigProvider>
          {/* Only show React Query Devtools in development */}
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AppProvider;
